// MaturaQuadraticBCSignsClosedTask.js
// Zadanie maturalne zamknięte – prawda/fałsz dla znaków współczynników b i c.
// Wspiera dwa warianty:
//  - "independent": w treści podane są oba miejsca zerowe,
//  - "linked": w treści podane jest jedno miejsce zerowe i oś symetrii.

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import TruthInput from "../../../../components/inputs/TruthInput";

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pick = (arr) => arr[randInt(0, arr.length - 1)];

// ================== pomocnicze formatowanie a ==================

function formatFractionLatex(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const absNum = Math.abs(num);
  const absDen = Math.abs(den);

  if (absDen === 1) {
    return (num < 0 ? "-" : "") + String(absNum);
  }

  const core = `\\dfrac{${absNum}}{${absDen}}`;
  return num < 0 ? `-${core}` : core;
}

function buildCoeffForX2(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const val = num / den;
  if (val === 1) return "";
  if (val === -1) return "-";
  return formatFractionLatex(num, den);
}

// ========================== GENERATOR ==========================

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 14.2:
 *   f(x) = 1/2 (x+4)(x−6) = 1/2 x^2 − x − 12  →  b = −1, c = −12.
 *   Oba zdania pytają, czy współczynnik jest dodatni → odpowiedzi: F, F.
 */
function createOriginalTask() {
  return {
    type: "MaturaQuadraticBCSignsClosedTask",
    data: {
      mode: "linked",
      axis: 1,
      r1: 6,
      r2: -4,
      givenRoot: 6,
      aNum: 1,
      aDen: 2,
      aValueLatex: "\\dfrac{1}{2}",
      aCoeffX2Latex: "\\dfrac{1}{2}",
      generalLatex: "f(x) = \\dfrac{1}{2}x^{2} + bx + c",
      bPos: false,
      cPos: false,
      askBPositive: true,
      askCPositive: true,
      bStatementTrue: false,
      cStatementTrue: false,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: "14.2",
    },
  };
}

// config.mode: "independent" | "linked"
// jeśli nie podane, domyślnie "independent"
// dodatkowo: config.sharedBase – dane wspólne z zadania 14.1 (Q1)
export function generateTask(config = {}) {
  if (config.variant === "original") return createOriginalTask();

  const mode = config.mode === "linked" ? "linked" : "independent";
  const sharedBase = config.sharedBase || null;

  // --- miejsca zerowe i oś ---
  let r1;
  let r2;
  let axis;
  let givenRoot;

  if (mode === "linked" && sharedBase) {
    // WARIANT POWIĄZANY – korzystamy z tej samej funkcji, co w Q1
    axis = sharedBase.axis;
    r1 = sharedBase.r1;
    r2 = sharedBase.r2;

    // jeśli przyjdzie givenRoot z Q1, używamy tego samego,
    // inaczej losujemy jedno z (r1, r2)
    if (typeof sharedBase.givenRoot === "number") {
      givenRoot = sharedBase.givenRoot;
    } else {
      givenRoot = pick([r1, r2]);
    }
  } else if (mode === "linked") {
    // STARY WARIANT LINKED – gdy nie podano sharedBase
    // Chcemy mieć całkowitą oś symetrii p oraz całkowite pierwiastki.
    axis = pick([-3, -2, -1, 0, 1, 2, 3]); // p
    const dist = pick([1, 2, 3]); // odległość od p
    r1 = axis + dist;
    r2 = axis - dist;
    givenRoot = pick([r1, r2]); // jedno z miejsc zerowych do treści
  } else {
    // wariant niezależny – oba pierwiastki podane w treści, oś nie pojawia się
    r1 = pick([-5, -4, -3, -2, -1, 1, 2, 3, 4, 5]);
    do {
      r2 = pick([-5, -4, -3, -2, -1, 1, 2, 3, 4, 5]);
    } while (r2 === r1);
    axis = (r1 + r2) / 2; // tylko pomocniczo, w treści nie używamy
    givenRoot = null;
  }

  // --- losowanie a z przedziału [-3,3] co 0.5, bez zera ---
  // a = num/2, num ∈ {-6,…,-1,1,…,6}
  const possibleNums = [-6, -5, -4, -3, -2, -1, 1, 2, 3, 4, 5, 6];
  let aNum;
  let aDen;

  if (sharedBase && typeof sharedBase.aNum === "number" && typeof sharedBase.aDen === "number") {
    // używamy tego samego a, co w zadaniu 14.1
    aNum = sharedBase.aNum;
    aDen = sharedBase.aDen;
  } else {
    // dotychczasowe losowanie
    aNum = pick(possibleNums);
    aDen = 2;

    if (aNum % 2 === 0) {
      aNum = aNum / 2;
      aDen = 1;
    }
  }

  const aValueLatex = formatFractionLatex(aNum, aDen);
  const aCoeffX2Latex = buildCoeffForX2(aNum, aDen);
  const generalLatex =
    aCoeffX2Latex === ""
      ? "f(x) = x^{2} + bx + c"
      : `f(x) = ${aCoeffX2Latex}x^{2} + bx + c`;

  const a = aNum / aDen;

  // --- współczynniki b i c z Viète'a ---
  // f(x) = a(x - r1)(x - r2) = ax^2 - a(r1 + r2)x + a r1 r2
  const b = -a * (r1 + r2);
  const c = a * (r1 * r2);

  const bPos = b > 0;
  const cPos = c > 0;

  // --- losowanie treści zdań P/F ---
  const askBPositive = Math.random() < 0.5;
  const askCPositive = Math.random() < 0.5;

  const bStatementTrue = askBPositive ? bPos : !bPos;
  const cStatementTrue = askCPositive ? cPos : !cPos;

  return {
    type: "MaturaQuadraticBCSignsClosedTask",
    data: {
      mode,
      axis,
      r1,
      r2,
      givenRoot,
      aNum,
      aDen,
      aValueLatex,
      aCoeffX2Latex,
      generalLatex,
      bPos,
      cPos,
      askBPositive,
      askCPositive,
      bStatementTrue,
      cStatementTrue,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_14_2" },
  };
}

// ======================= RENDER TASK (TREŚĆ) =======================

export function renderTask({ data }) {
  const {
    mode,
    axis,
    givenRoot,
    r1,
    r2,
    generalLatex,
    askBPositive,
    askCPositive,
  } = data;

  const bAdjective = askBPositive ? "dodatnią" : "ujemną";
  const cAdjective = askCPositive ? "dodatnią" : "ujemną";

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>

      <p>
        Funkcja kwadratowa <InlineMath math={"f"} /> jest określona wzorem{" "}
        <InlineMath math={generalLatex} />, gdzie{" "}
        <InlineMath math={"b, c"} /> są liczbami rzeczywistymi.
      </p>

      {mode === "linked" ? (
        <p>
          Jednym z miejsc zerowych funkcji <InlineMath math={"f"} /> jest liczba{" "}
          <InlineMath math={`${givenRoot}`} />. W kartezjańskim układzie
          współrzędnych prosta o równaniu{" "}
          <InlineMath math={`x = ${axis}`} /> jest osią symetrii wykresu
          funkcji <InlineMath math={"f"} />.
        </p>
      ) : (
        <p>
          Miejscami zerowymi funkcji <InlineMath math={"f"} /> są liczby{" "}
          <InlineMath math={`${r1}`} /> oraz <InlineMath math={`${r2}`} />.
        </p>
      )}

      <p style={{ marginTop: 8 }}>Oceń prawdziwość poniższych zdań.</p>

      <p style={{ marginTop: 12 }}>
        <InlineMath math="1." /> Współczynnik <InlineMath math={"b"} /> we wzorze funkcji{" "}
        <InlineMath math={"f"} /> jest liczbą {bAdjective}.
      </p>

      <div style={{ marginLeft: 8 }}>
        <div id="truth-b" />
      </div>

      <p style={{ marginTop: 14 }}>
        <InlineMath math="2." /> Współczynnik <InlineMath math={"c"} /> we wzorze funkcji{" "}
        <InlineMath math={"f"} /> jest liczbą {cAdjective}.
      </p>

      <div style={{ marginLeft: 8 }}>
        <div id="truth-c" />
      </div>
    </div>
  );
}

// ======================= RENDER INPUT =======================
// userAnswer = { bUserTrue: boolean, cUserTrue: boolean }

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const bVal =
    userAnswer?.bUserTrue === true
      ? "P"
      : userAnswer?.bUserTrue === false
      ? "F"
      : null;

  const cVal =
    userAnswer?.cUserTrue === true
      ? "P"
      : userAnswer?.cUserTrue === false
      ? "F"
      : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Input dla zdania 1 (b) */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>1.</span>
        <TruthInput
          value={bVal}
          disabled={disabled}
          onChange={(v) =>
            onChange({
              ...userAnswer,
              bUserTrue: v === "P",
            })
          }
        />
      </div>

      {/* Input dla zdania 2 (c) */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ minWidth: 18 }}>2.</span>
        <TruthInput
          value={cVal}
          disabled={disabled}
          onChange={(v) =>
            onChange({
              ...userAnswer,
              cUserTrue: v === "P",
            })
          }
        />
      </div>
    </div>
  );
}

// ======================= CHECK ANSWER =======================

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer) return false;

  return (
    userAnswer.bUserTrue === data.bStatementTrue &&
    userAnswer.cUserTrue === data.cStatementTrue
  );
}

// ======================= RENDER ANSWER =======================

export function renderAnswer({ data }) {
  const bLatex = data.bStatementTrue ? "\\text{P}" : "\\text{F}";
  const cLatex = data.cStatementTrue ? "\\text{P}" : "\\text{F}";

  return (
    <div style={{ textAlign: "left" }}>
      <p>
        <InlineMath math="1." /> <InlineMath math={bLatex} />
        <br />
        <InlineMath math="2." /> <InlineMath math={cLatex} />
      </p>
    </div>
  );
}

// ======================= RENDER EXPLANATION =======================

export function renderExplanation({ data }) {
  const {
    mode,
    axis,
    givenRoot,
    r1,
    r2,
    aNum,
    aDen,
    aValueLatex,
    aCoeffX2Latex,
    bPos,
    cPos,
    askBPositive,
    askCPositive,
    bStatementTrue,
    cStatementTrue,
  } = data;

  const x1 = r1;
  const x2 = r2;

  const bSignText = bPos ? "dodatni" : "ujemny";
  const cSignText = cPos ? "dodatni" : "ujemny";

  const bSentence = askBPositive
    ? <>Współczynnik <InlineMath math="b" /> jest liczbą dodatnią.</>
    : <>Współczynnik <InlineMath math="b" /> jest liczbą ujemną.</>;

  const cSentence = askCPositive
    ? <>Współczynnik <InlineMath math="c" /> jest liczbą dodatnią.</>
    : <>Współczynnik <InlineMath math="c" /> jest liczbą ujemną.</>;

  const bSentenceLatex = bStatementTrue ? "\\text{P}" : "\\text{F}";
  const cSentenceLatex = cStatementTrue ? "\\text{P}" : "\\text{F}";

  // pomocnicze formatowanie: "+ 2x", "- 5x", "+ 7", "- 3/2" itd.
  function signedTerm(num, den, variable) {
    if (num === 0) return "";
    const sign = num > 0 ? "+" : "-";
    let absNum = Math.abs(num);
    let absDen = Math.abs(den);

    if (variable) {
      // współczynnik przy x
      if (absDen === 1 && absNum === 1) {
        return `${sign} ${variable}`;
      }
      const frac = formatFractionLatex(absNum, absDen);
      return `${sign} ${frac}${variable}`;
    } else {
      // liczba bez x
      const frac =
        absDen === 1 ? String(absNum) : formatFractionLatex(absNum, absDen);
      return `${sign} ${frac}`;
    }
  }

  return (
    <div style={{ textAlign: "left" }}>
      {mode === "linked" && (
        <>
          <p>
            W treści zadania podano jedno miejsce zerowe oraz oś symetrii.{" "}
            Oznaczmy podane miejsce zerowe jako{" "}
            <InlineMath math={`x_1 = ${givenRoot}`} /> oraz oś symetrii jako{" "}
            <InlineMath math={`p = ${axis}`} />.
          </p>

          <p>
            Z karty wzorów wiemy, że dla funkcji kwadratowej obowiązuje zależność:
          </p>

          <BlockMath math={"p = \\dfrac{x_1 + x_2}{2}"} />

          <p>Podstawiamy nasze dane i mnożymy obustronnie przez <InlineMath math="2" />:</p>

          <BlockMath math={`${axis} = \\dfrac{x_1 + x_2}{2}`} />
          <BlockMath math={`2\\cdot ${axis} = x_1 + x_2`} />
          <BlockMath math={"x_2 = 2p - x_1"} />

          <p>
            W naszym zadaniu daje to konkretnie{" "}
            <InlineMath math={`x_1 = ${x1},\\ x_2 = ${x2}`} /> oraz{" "}
            <InlineMath math={`a = ${aValueLatex}`} />.
          </p>

          <p>
            Dalej możemy skorzystać z zależności Viète&apos;a dla funkcji{" "}
            <InlineMath math={"f(x) = ax^2 + bx + c"} />:
          </p>

          <BlockMath math={"b = -a(x_1 + x_2),\\quad c = a x_1 x_2."} />

          <p>Po podstawieniu tych wartości dostajemy konkretne <InlineMath math="b" /> i <InlineMath math="c" />, a więc ich znaki.</p>
        </>
      )}

      {mode === "independent" && (
        <>
          {/* KROK 1 */}
          <p>
            <strong>Krok 1.</strong> Zapisujemy funkcję w postaci iloczynowej na
            podstawie podanych miejsc zerowych.
          </p>

          <BlockMath
            math={`f(x) = a(x - x_1)(x - x_2) = ${aValueLatex}(x - (${x1}))(x - (${x2}))`}
          />

          {(() => {
            // A i B po uproszczeniu nawiasów: (x - x1)(x - x2) = (x + A)(x + B)
            const A = -x1;
            const B = -x2;

            // współczynniki w postaci ogólnej (liczbowo)
            const linCoeff = A + B; // przy x
            const constCoeff = A * B; // wyraz wolny

            // b i c w postaci ogólnej po pomnożeniu przez a
            const bNum = aNum * linCoeff;
            const bDen = aDen;
            const cNum = aNum * constCoeff;
            const cDen = aDen;

            const bLatexValue = formatFractionLatex(bNum, bDen);
            const cLatexValue = formatFractionLatex(cNum, cDen);

            // x^2 + ... w uproszczonej wersji (przed mnożeniem przez a)
            const linTermBeforeA = signedTerm(linCoeff, 1, "x");
            const constTermBeforeA = signedTerm(constCoeff, 1, "");

            const polyBeforeA =
              linCoeff === 0 && constCoeff === 0
                ? "x^{2}"
                : `x^{2} ${linTermBeforeA} ${constTermBeforeA}`;

            // końcowa postać ogólna po pomnożeniu przez a
            const aX2 =
              aCoeffX2Latex === "" ? "x^{2}" : `${aCoeffX2Latex}x^{2}`;
            const bTerm = signedTerm(bNum, bDen, "x");
            const cTerm = signedTerm(cNum, cDen, "");

            return (
              <>
                {/* KROK 2 */}
                <p style={{ marginTop: 12 }}>
                  <strong>Krok 2.</strong> Uprośćmy nawiasy i wymnóżmy je, a
                  następnie wymnóżmy przez <InlineMath math={"a"} />.
                </p>

                {/* uproszczenie nawiasów */}
                <BlockMath
                  math={`(x - (${x1}))(x - (${x2})) = (x ${A >= 0 ? "+" : "-"} ${Math.abs(
                    A
                  )})(x ${B >= 0 ? "+" : "-"} ${Math.abs(B)})`}
                />

                {/* rozpisane FOIL na konkretnych liczbach */}
                <BlockMath
                  math={`= x^{2} ${signedTerm(A, 1, "x")} ${
                    signedTerm(B, 1, "x")
                  } + ${A}\\cdot(${B})`}
                />

                {/* uproszczony trójmian bez a */}
                <BlockMath math={`= ${polyBeforeA}`} />

                <p>
                  Następnie mnożymy cały trójmian przez{" "}
                  <InlineMath math={aValueLatex} />:
                </p>

                <BlockMath
                  math={`f(x) = ${aValueLatex}\\left(${polyBeforeA}\\right)`}
                />
                <BlockMath math={`f(x) = ${aX2} ${bTerm} ${cTerm}`} />

                {/* KROK 3 */}
                <p style={{ marginTop: 12 }}>
                  <strong>Krok 3.</strong> Z tej postaci ogólnej{" "}
                  <InlineMath math={"f(x) = ax^2 + bx + c"} /> odczytujemy
                  współczynniki <InlineMath math={"b"} /> i{" "}
                  <InlineMath math={"c"} />:
                </p>

                <BlockMath
                  math={`b = ${bLatexValue},\\qquad c = ${cLatexValue}.`}
                />

                <p>
                  Widzimy więc, że znak <InlineMath math={"b"} /> jest{" "}
                  {bSignText}, a znak <InlineMath math={"c"} /> jest{" "}
                  {cSignText}.
                </p>
              </>
            );
          })()}
        </>
      )}

      <p>Z tego wynika prawdziwość zdań z treści zadania:</p>
      <ul>
        <li>
          {bSentence} – odpowiedź <InlineMath math={bSentenceLatex} /> (
          <strong>{bStatementTrue ? "prawda" : "fałsz"}</strong>).
        </li>
        <li>
          {cSentence} – odpowiedź <InlineMath math={cSentenceLatex} /> (
          <strong>{cStatementTrue ? "prawda" : "fałsz"}</strong>).
        </li>
      </ul>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const bVal =
    userAnswer?.bUserTrue === true
      ? "P"
      : userAnswer?.bUserTrue === false
      ? "F"
      : null;

  const cVal =
    userAnswer?.cUserTrue === true
      ? "P"
      : userAnswer?.cUserTrue === false
      ? "F"
      : null;

  if (!userAnswer || (bVal === null && cVal === null)) {
    return <i>(brak odpowiedzi)</i>;
  }

  const bLatex = bVal ? `\\text{${bVal}}` : "-";
  const cLatex = cVal ? `\\text{${cVal}}` : "-";

  return (
    <div style={{ marginTop: "1rem", textAlign: "left" }}>
      <p>Twoja odpowiedź:</p>
      <p>
        1. <InlineMath math={bLatex} />
        <br />
        2. <InlineMath math={cLatex} />
      </p>
    </div>
  );
}
